import { useToast } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import {
  OrganizationContext,
  OrganizationContextProps,
} from '@web-app/hocs/organization';
import { MealPlanInstructionsEntityResponse } from '@web-app/server/api/types';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useContext } from 'react';

import { useGenerateDetailedMealPlan } from './mutations/meal-plan/generate-detailed-meal-plan';
import { useGenerateMealPlanInstructions } from './mutations/meal-plan/generate-meal-plan-instructions';
import { useGenerateMealPlanSummary } from './mutations/meal-plan/generate-meal-plan-summary';
import { useUpdateMealPlanInstructions } from './mutations/meal-plan/update-meal-plan-instructions';
import { useUpdateMealPlanSummary } from './mutations/meal-plan/update-meal-plan-summary';

// ============================================================================
// Types
// ============================================================================

export interface UseMealPlanGenerationReturn {
  // Mutations
  generateInstructions: () => Promise<MealPlanInstructionsEntityResponse | null>;
  generateSummary: () => Promise<boolean>;
  generateDetailedPlan: () => Promise<boolean>;

  // Loading states
  isGeneratingInstructions: boolean;
  isGeneratingSummary: boolean;
  isGeneratingDetailedPlan: boolean;
}

// ============================================================================
// Hook
// ============================================================================

interface UseMealPlanGenerationOptions {
  usePreviousAppointment?: boolean;
  /** When true, suppress success/error toasts for instructions generation (e.g. auto-generation). */
  silentInstructions?: boolean;
  /** When true, delete existing record before regenerating instructions. */
  forceInstructions?: boolean;
}

/**
 * Hook for meal plan generation mutations.
 *
 * Handles generating instructions, summary, and detailed plan.
 * Each generation saves the current step's data before generating the next.
 * dailyRoomName is provided via MealPlanScope by the parent component.
 */
export function useMealPlanGeneration(
  options?: UseMealPlanGenerationOptions,
): UseMealPlanGenerationReturn {
  const {
    usePreviousAppointment = false,
    silentInstructions = false,
    forceInstructions = false,
  } = options ?? {};
  const toast = useToast({
    position: 'bottom-left',
    isClosable: true,
    duration: 5000,
  });

  const {
    dailyRoomNameAtom,
    healthieClientIdAtom,
    healthieProviderIdAtom,
    previousAppointmentIdAtom,
    detailedMealPlanEditModeAtom,
    isMealPlanBusyAtom,
    mealPlanInstructionsAtom,
    mealPlanSummaryAtom,
    refetchDetailedMealPlanAtom,
    refetchMealPlanInstructionsAtom,
    refetchMealPlanSummaryAtom,
  } = useMolecule(mealPlanMolecule);

  // Room name and healthie IDs for API calls
  const dailyRoomName = useAtomValue(dailyRoomNameAtom);
  const healthieClientId = useAtomValue(healthieClientIdAtom);
  const healthieProviderId = useAtomValue(healthieProviderIdAtom);
  const previousAppointmentId = useAtomValue(previousAppointmentIdAtom);

  const { organizationId } =
    useContext<OrganizationContextProps>(OrganizationContext);
  const orgId =
    organizationId !== undefined ? String(organizationId) : undefined;

  // Data atoms
  const [mealPlanInstructions, setMealPlanInstructions] = useAtom(
    mealPlanInstructionsAtom,
  );
  const mealPlanSummary = useAtomValue(mealPlanSummaryAtom);

  // Loading state
  const setIsMealPlanBusy = useSetAtom(isMealPlanBusyAtom);

  // Edit mode (cleared before regenerating detailed plan)
  const setDetailedMealPlanEditMode = useSetAtom(detailedMealPlanEditModeAtom);

  // Refetch action atoms — stable setter references safe for useCallback deps.
  const refetchInstructions = useSetAtom(refetchMealPlanInstructionsAtom);
  const refetchSummary = useSetAtom(refetchMealPlanSummaryAtom);
  const refetchDetailedPlan = useSetAtom(refetchDetailedMealPlanAtom);

  // Existing mutation hooks
  const {
    mutateAsync: generateMealPlanInstructionsMutation,
    isPending: isGeneratingInstructions,
  } = useGenerateMealPlanInstructions();

  const {
    mutateAsync: generateMealPlanSummaryMutation,
    isPending: isGeneratingSummary,
  } = useGenerateMealPlanSummary();

  const {
    mutateAsync: generateDetailedMealPlanMutation,
    isPending: isGeneratingDetailedPlan,
  } = useGenerateDetailedMealPlan();

  const { mutateAsync: updateMealPlanInstructionsMutation } =
    useUpdateMealPlanInstructions();

  const { mutateAsync: updateMealPlanSummaryMutation } =
    useUpdateMealPlanSummary();

  // ============================================================================
  // Generate Instructions
  // ============================================================================

  const triggerType = forceInstructions ? 'manual' : 'auto';

  const generateInstructions =
    useCallback(async (): Promise<MealPlanInstructionsEntityResponse | null> => {
      if (!dailyRoomName) {
        datadogLogs.logger.warn(
          'Cannot generate instructions: no dailyRoomName',
          logContext('meal-plan', 'generate-instructions', {
            triggerType,
            orgId,
          }),
        );
        return null;
      }

      setIsMealPlanBusy(true);

      try {
        const result = await generateMealPlanInstructionsMutation({
          dailyRoomName,
          healthieClientId,
          healthieProviderId,
          sourceAppointmentId: usePreviousAppointment
            ? (previousAppointmentId ?? undefined)
            : undefined,
          force: forceInstructions,
        });
        setMealPlanInstructions(result);
        await refetchInstructions();
        setMealPlanInstructions(undefined); // clear override so derived atom falls through to query data

        if (!silentInstructions) {
          toast({
            title: 'Meal plan instructions generated!',
            status: 'success',
            duration: 3000,
          });
        }

        return result;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        datadogLogs.logger.error(
          'Failed to generate instructions',
          logContext('meal-plan', 'generate-instructions', {
            dailyRoomName,
            triggerType,
            force: forceInstructions,
            orgId,
            err: { message: err.message, stack: err.stack },
          }),
        );

        if (!silentInstructions) {
          toast({
            title:
              'Failed to generate meal plan instructions. Please try again.',
            status: 'error',
            duration: 5000,
          });
        }

        return null;
      } finally {
        setIsMealPlanBusy(false);
      }
    }, [
      dailyRoomName,
      healthieClientId,
      healthieProviderId,
      previousAppointmentId,
      usePreviousAppointment,
      silentInstructions,
      forceInstructions,
      triggerType,
      orgId,
      generateMealPlanInstructionsMutation,
      refetchInstructions,
      setIsMealPlanBusy,
      setMealPlanInstructions,
      toast,
    ]);

  // ============================================================================
  // Generate Summary
  // ============================================================================

  const generateSummary = useCallback(async (): Promise<boolean> => {
    if (!dailyRoomName) {
      datadogLogs.logger.warn(
        'Cannot generate summary: no dailyRoomName',
        logContext('meal-plan', 'generate-summary', { orgId }),
      );
      return false;
    }

    setIsMealPlanBusy(true);

    try {
      // Save current instructions first if they exist
      if (mealPlanInstructions) {
        await updateMealPlanInstructionsMutation({
          ...mealPlanInstructions,
          healthieClientId,
          healthieProviderId,
        });
      }

      await generateMealPlanSummaryMutation({
        dailyRoomName,
        healthieClientId,
        healthieProviderId,
      });
      await refetchSummary();

      toast({
        title: 'Meal plan summary has been generated!',
        status: 'success',
        duration: 3000,
      });

      return true;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      datadogLogs.logger.error(
        'Failed to generate summary',
        logContext('meal-plan', 'generate-summary', {
          dailyRoomName,
          orgId,
          err: { message: err.message, stack: err.stack },
        }),
      );

      toast({
        title: 'Failed to generate the meal plan summary. Please try again.',
        status: 'error',
        duration: 5000,
      });

      return false;
    } finally {
      setIsMealPlanBusy(false);
    }
  }, [
    dailyRoomName,
    healthieClientId,
    healthieProviderId,
    orgId,
    generateMealPlanSummaryMutation,
    mealPlanInstructions,
    setIsMealPlanBusy,
    refetchSummary,
    toast,
    updateMealPlanInstructionsMutation,
  ]);

  // ============================================================================
  // Generate Detailed Plan
  // ============================================================================

  const generateDetailedPlan = useCallback(async (): Promise<boolean> => {
    if (!dailyRoomName) {
      datadogLogs.logger.warn(
        'Cannot generate detailed plan: no dailyRoomName',
        logContext('meal-plan', 'generate-detailed-plan', { orgId }),
      );
      return false;
    }

    setIsMealPlanBusy(true);

    try {
      // Save current summary first if it exists
      if (mealPlanSummary) {
        await updateMealPlanSummaryMutation(mealPlanSummary);
      }

      // Clear edit mode if active
      setDetailedMealPlanEditMode(false);

      await generateDetailedMealPlanMutation({
        dailyRoomName,
        healthieClientId,
        healthieProviderId,
      });
      await refetchDetailedPlan();

      toast({
        title: 'Meal plan recipes have been generated!',
        status: 'success',
        duration: 3000,
      });

      return true;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      datadogLogs.logger.error(
        'Failed to generate detailed plan',
        logContext('meal-plan', 'generate-detailed-plan', {
          dailyRoomName,
          orgId,
          err: { message: err.message, stack: err.stack },
        }),
      );

      toast({
        title: 'Failed to generate the meal plan. Please try again.',
        status: 'error',
        duration: 5000,
      });

      return false;
    } finally {
      setIsMealPlanBusy(false);
    }
  }, [
    dailyRoomName,
    healthieClientId,
    healthieProviderId,
    orgId,
    refetchDetailedPlan,
    generateDetailedMealPlanMutation,
    mealPlanSummary,
    setDetailedMealPlanEditMode,
    setIsMealPlanBusy,
    toast,
    updateMealPlanSummaryMutation,
  ]);

  return {
    // Mutations
    generateInstructions,
    generateSummary,
    generateDetailedPlan,

    // Loading states
    isGeneratingInstructions,
    isGeneratingSummary,
    isGeneratingDetailedPlan,
  };
}
